import { stripHtml } from '@/utils';

export default class Card {
  constructor(obj) {
    this.assetType = obj.assetType;
    this.assetId = obj.assetId;
    this.description = obj.description;
    this.featuredImageId = obj.featuredImageId;
    this.id = obj.id;
    this.order = obj.order;
    this.title = obj.title;
    try {
      this.config = JSON.parse(obj.config);
    } catch {
      this.config = {};
    }
    this.route = this.generateRoute();
  }

  static getterMap = {
    Attachment: 'attachments/get',
    Category: 'categories/categories',
    CategoryType: 'categoryTypes/all',
    Deck: 'decks/get',
    Grouping: 'groupings/all',
    LearningModule: 'learningModules/get',
    Topic: 'topics/topics',
  };

  // Attachment icons based on the media type of the attachment and not included here
  static iconMap = {
    Deck: 'series',
    LearningModule: 'learning-module',
  };

  // Attachments open in a modal and don't have a specific route
  // Groupings routes are different based on the grouping type and not included here
  // Test simulation are handled by the card component and not included here
  static routeMap = {
    Attachment: { name: 'decks#attachment', paramName: 'cardId' },
    Category: { name: 'categories#index', paramName: 'categoryId' },
    CategoryType: { name: 'categoryTypes#index', paramName: 'categoryTypeId' },
    Deck: { name: 'decks#show', paramName: 'deckId' },
    LearningModule: { name: 'learningModules#start', paramName: 'learningModuleId' },
    Topic: { name: 'decks#topic', paramName: 'cardId' },
    attachment_categories: { name: 'attachments#index', queryName: 'nav' },
    topic_categories: { name: 'topics#index', queryName: 'nav' },
    terminology: { name: 'categories#flashcard', paramName: 'categoryId' },
    term_categories: { name: 'categories#index', queryName: 'nav'},
    wux_test_simulation: { name: 'sims#show', paramName: 'simId' },
    flashcards: { name: 'categories#flashcard', paramName: 'categoryId' },
    test_simulations: { name: 'testSimulations#start', paramName: 'categoryId' },
    scenario_categories: { name: 'scenarios#index', paramName: 'categoryTypeId' },
  };

  get getter() {
    return Card.getterMap[this.assetType];
  }

  get icon() {
    return Card.iconMap[this.assetType];
  }

  get isAttachmentCard() {
    return this.assetType === 'Attachment';
  }

  get isDeckCard() {
    return this.assetType === 'Deck';
  }

  get isCategoryCard() {
    return this.assetType === 'Category';
  }

  get isGroupingCard() {
    return this.assetType === 'Grouping';
  }

  get isLearningModuleCard() {
    return this.assetType === 'LearningModule';
  }

  get isTestSimulationCard() {
    return this.navigationDestination?.startsWith('wux_test_simulation');
  }

  get isLegacyTestSimulationCard() {
    return this.navigationDestination?.startsWith('test_simulations');
  }

  get isTopicCard() {
    return this.assetType === 'Topic';
  }

  get listRowType() {
    return `${this.assetType}ListRow`;
  }

  get isCategoryTypeCard() {
    return this.assetType === 'CategoryType';
  }
  // Does this card point to a single asset or to an asset that contains multiple assets?
  get singular() {
    return this.isAttachmentCard || this.isLearningModuleCard || this.isTopicCard;
  }

  // Does this card point to an asset that contains multiple assets?
  get container() {
    return this.isDeckCard || this.isGroupingCard;
  }

  get escapedDescription() {
    return stripHtml(this.description);
  }

  get navigationDestination() {
    return this.config?.navigation_destination;
  }

  get navigationItemId() {
    return this.config?.navigation_item_id;
  }

  generateRoute() {
    const itemDestinations = ['terminology', 'flashcards'];
    let baseRoute;
    if (this.navigationDestination === 'term_categories' || this.navigationDestination === 'attachment_categories' || this.navigationDestination === 'topic_categories') {
      baseRoute = Card.routeMap[this.navigationDestination];
    } else if (this.isLegacyTestSimulationCard) {
      baseRoute = Card.routeMap['test_simulations'];
    } else if (this.isTestSimulationCard) {
      baseRoute = Card.routeMap['wux_test_simulation'];
    } else if (this.isCategoryTypeCard && this.navigationDestination === 'scenario_categories') {
      baseRoute = Card.routeMap['scenario_categories'];
    } else if (this.isCategoryCard && itemDestinations.includes(this.navigationDestination)) {
      baseRoute = Card.routeMap[this.navigationDestination];
    } else {
      baseRoute = Card.routeMap[this.assetType];
    }

    if (baseRoute) {
      let route = {
        name: baseRoute.name,
      };

      if (this.isTopicCard || this.isAttachmentCard) {
        route.params = {
          [baseRoute.paramName]: this.id,
        };
      } else if (baseRoute.queryName === 'nav') {
        route.query = {
          [baseRoute.queryName]: this.navigationItemId,
        };
      } else {
        route.params = {
          [baseRoute.paramName]: this.assetId,
        };
      }

      return route;
    }
  }
}
